import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import useSWR from 'swr';
import {useTranslation} from 'react-i18next';
import {isMobile} from 'react-device-detect';

import api, {getURL} from 'api';
import {useAuth} from '../../context/auth';
import {useReservation} from 'context/reservation';
import {useStoredURLParams} from 'context/storedURLParams';
import {
  Statuses,
  useErrorModal,
  useIsMounted,
  useModalControls,
  useScreenResize,
  useStatus,
} from 'utils/hooks';
import {MOBILE_SEARCH_STEPS, SEARCH_RESERVATION_FORM_NAMES} from './constants';
import {IS_UNIVERSAL_SESSION_PARAM} from '../../App';
import {PATHS} from '../../Routes';
import {device} from '../../styled/device';
import PreviewBackgroundImage from '../../components/PreviewBackgroundImage';
import Page from '../../components/acommon/Page';
import ReservationNotFoundModal from './ReservationNotFoundModal';
import {SearchByBookingReferenceForm} from './components/SearchByBookingReferenceForm';
import {SearchByBookingDetailsForm} from './components/SearchByBookingDetailsForm';
import {DimensionsWrapper} from '../../styled/common';
import {
  Container,
  Content,
  HorizontalLine,
  Item,
  Subtitle,
  Title,
  VerticalLine,
} from './styled';

type LocationType = {
  l?: string;
  user?: string;
  housingId: string;
};

export type FormTypes = {
  [SEARCH_RESERVATION_FORM_NAMES.lead_email]: string;
  [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: Date | string;
  [SEARCH_RESERVATION_FORM_NAMES.booking_reference]: string;
};

function SearchReservationScreen() {
  const {t} = useTranslation();
  const {isLoading: isLoadingToken} = useAuth();
  const {refreshReservation} = useReservation();
  const {setNewReservationId} = useStoredURLParams();
  const {ErrorModal, displayError} = useErrorModal();
  const {isLoading, setStatus} = useStatus();
  const payloadFormRef = React.useRef<Partial<FormTypes>>();
  const location = useLocation<LocationType>();
  const history = useHistory();
  const shortHousingId = location?.state?.housingId;
  const l = location?.state?.l;
  const user = location?.state?.user;
  const {isMatch} = useScreenResize(device.laptop);
  const isMobileMode = isMatch || isMobile;
  const [mobileStep, setMobileStep] = React.useState<MOBILE_SEARCH_STEPS>(
    MOBILE_SEARCH_STEPS.bookingReference,
  );

  const {
    isOpen: isOpenReservationNotFoundModal,
    openModal: openReservationNotFoundModal,
    closeModal: closeReservationNotFoundModal,
  } = useModalControls();

  const isMounted = useIsMounted();
  const {data: housing} = useSWR(
    shortHousingId && !isLoadingToken ? getURL(`housings/${shortHousingId}/`) : null,
  );
  const [foundReservationId, setFoundReservationId] = React.useState('');
  const logo = housing?.picture?.src;
  const title = housing?.name;
  const country = housing?.location?.country?.code;

  const searchReservationByDetails = async (
    payload: Omit<FormTypes, SEARCH_RESERVATION_FORM_NAMES.booking_reference>,
  ) => {
    const id = shortHousingId;

    const {data, error} = await api.universalLink.searchReservation(id, payload);

    if (!isMounted.current) {
      return;
    }

    if (data) {
      const reservationId = data.reservation_id;
      
      if (reservationId) {
        setNewReservationId(reservationId);
        setFoundReservationId(reservationId);
      } else {
        payloadFormRef.current = payload;
        openReservationNotFoundModal();
        return setStatus(Statuses.idle);
      }
    }

    if (error) {
      setStatus('error');
      displayError(error);
    }
  };

  const searchReservationByBookingReference = async (
    payload: Pick<FormTypes, SEARCH_RESERVATION_FORM_NAMES.booking_reference>,
  ) => {
    const id = shortHousingId;
    const {booking_reference} = payload;

    const {data, error} = await api.reservations.getAll(
      `housing_id=${id}&booking_reference=${encodeURIComponent(booking_reference)}`,
    );

    if (!isMounted.current) {
      return;
    }

    if (data) {
      const reservationId = data?.results?.[0]?.id;

      if (reservationId) {
        setNewReservationId(reservationId);
        setFoundReservationId(reservationId);
      } else {
        payloadFormRef.current = payload;
        openReservationNotFoundModal();
        return setStatus(Statuses.idle);
      }
    }

    if (error) {
      setStatus('error');
      displayError(error);
    }
  };

  const getComputedTitle = () => {
    if (isMobileMode) {
      return mobileStep === MOBILE_SEARCH_STEPS.bookingReference
        ? t('welcome')
        : t('lets_find_your_booking');
    }
    return t('welcome_to_housing_name', {housingName: title});
  };

  const getComputedSubTitle = () => {
    if (isMobileMode && mobileStep === MOBILE_SEARCH_STEPS.dateAndEmail) {
      return t('you_have_two_ways') + ':';
    }
    return t('welcome_to_housing_name_subtitle');
  };

  React.useLayoutEffect(
    function fetchReservation() {
      if (foundReservationId) {
        const refetchReservation = async () => {
          await refreshReservation();
          sessionStorage.removeItem(IS_UNIVERSAL_SESSION_PARAM);
          setStatus('success');

          history.push(`/?reservation-id=${foundReservationId}&l=${l}&user=${user}`, {
            fromHousingFlow: true,
          });
        };

        refetchReservation();
      }
    },
    [foundReservationId, history, user, l, refreshReservation, setStatus],
  );

  const handleChangeMobileStep = (step: MOBILE_SEARCH_STEPS) => () => setMobileStep(step);
  const handleNextFromNotFoundModal = () => {
    const payload = payloadFormRef.current;

    history.push(PATHS.newReservation, {
      startDate: payload?.[SEARCH_RESERVATION_FORM_NAMES.check_in_date],
      email: payload?.[SEARCH_RESERVATION_FORM_NAMES.lead_email],
      housingId: shortHousingId,
      logo,
      title,
      country,
      l,
      user,
    });
  };

  return (
    <Page showFooter footerTitle={title}>
      <DimensionsWrapper>
        <PreviewBackgroundImage>
          <Container>
            <Title>{getComputedTitle()}</Title>
            <Subtitle>{getComputedSubTitle()}</Subtitle>
            {mobileStep === MOBILE_SEARCH_STEPS.dateAndEmail && <HorizontalLine />}
            <Content>
              <Item
                isMobileMode={isMobileMode}
                isVisible={mobileStep === MOBILE_SEARCH_STEPS.bookingReference}
              >
                <SearchByBookingReferenceForm
                  onChangeMobileStep={handleChangeMobileStep}
                  onSearch={searchReservationByBookingReference}
                  isMobileMode={isMobileMode}
                  isLoading={isLoading}
                  setStatus={setStatus}
                />
              </Item>
              <Item>
                <span>{t('or')}</span>
                <VerticalLine />
              </Item>
              <Item
                isMobileMode={isMobileMode}
                isVisible={mobileStep === MOBILE_SEARCH_STEPS.dateAndEmail}
              >
                <SearchByBookingDetailsForm
                  onChangeMobileStep={handleChangeMobileStep}
                  onSearch={searchReservationByDetails}
                  isMobileMode={isMobileMode}
                  isLoading={isLoading}
                  setStatus={setStatus}
                />
              </Item>
            </Content>
          </Container>
          <ReservationNotFoundModal
            open={isOpenReservationNotFoundModal}
            onNext={handleNextFromNotFoundModal}
            onClose={closeReservationNotFoundModal}
          />
          <ErrorModal />
        </PreviewBackgroundImage>
      </DimensionsWrapper>
    </Page>
  );
}

export {SearchReservationScreen};
